
import React, { createContext, useContext, useState, useEffect } from 'react';

// 🧠 USER KNOWLEDGE CONTEXT PROVIDER
// Provides user knowledge tracking across the entire application

const UserKnowledgeContext = createContext(undefined);

export const UserKnowledgeProvider = ({ children }) => {
    const [userKnowledge, setUserKnowledge] = useState(() => {
        // Initialize from localStorage or create new profile
        const saved = localStorage.getItem('userKnowledgeProfile');
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (error) {
                console.error('Error parsing saved user knowledge:', error);
            }
        }

        // Create new profile with basic prerequisites met for beginner exercises
        return {
            grammar_points: {
                basic_verb_conjugation: { mastered: true, attempts: 0, accuracy: 1.0 }, // Assume basic knowledge
                polite_speech_endings: { mastered: true, attempts: 0, accuracy: 1.0 }, // Assume basic knowledge
                informal_speech_endings: { mastered: false, attempts: 0, accuracy: 0 },
                honorific_forms: { mastered: false, attempts: 0, accuracy: 0 },
                past_tense_markers: { mastered: false, attempts: 0, accuracy: 0 }
            },
            vocabulary: {
                basic_verbs: { mastered: true, attempts: 0, accuracy: 1.0 }, // Assume basic knowledge
                family_terms: { mastered: true, attempts: 0, accuracy: 1.0 }, // Assume basic knowledge
                time_expressions: { mastered: true, attempts: 0, accuracy: 1.0 } // Assume basic knowledge
            },
            skills: {
                verb_stem_recognition: { mastered: true, attempts: 0, accuracy: 1.0 }, // Assume basic knowledge
                speech_level_awareness: { mastered: false, attempts: 0, accuracy: 0 }
            },
            overall_level: 'beginner',
            last_updated: new Date().toISOString()
        };
    });

    // Save to localStorage whenever user knowledge changes
    useEffect(() => {
        localStorage.setItem('userKnowledgeProfile', JSON.stringify(userKnowledge));
    }, [userKnowledge]);

    const updateUserKnowledge = (exercise, isCorrect, _responseTime) => {
        setUserKnowledge(prev => {
            const updated = { ...prev };

            // Update grammar points
            if (exercise.prerequisites?.required_grammar_points) {
                exercise.prerequisites.required_grammar_points.forEach(grammarPoint => {
                    if (!updated.grammar_points[grammarPoint]) {
                        updated.grammar_points[grammarPoint] = { mastered: false, attempts: 0, accuracy: 0 };
                    }

                    const point = updated.grammar_points[grammarPoint];
                    point.attempts++;
                    point.accuracy = ((point.accuracy * (point.attempts - 1)) + (isCorrect ? 1 : 0)) / point.attempts;

                    if (point.accuracy > 0.8 && point.attempts >= 3) {
                        point.mastered = true;
                    }
                });
            }

            // Update vocabulary
            if (exercise.prerequisites?.required_vocabulary) {
                exercise.prerequisites.required_vocabulary.forEach(vocabCategory => {
                    if (!updated.vocabulary[vocabCategory]) {
                        updated.vocabulary[vocabCategory] = { mastered: false, attempts: 0, accuracy: 0 };
                    }

                    const vocab = updated.vocabulary[vocabCategory];
                    vocab.attempts++;
                    vocab.accuracy = ((vocab.accuracy * (vocab.attempts - 1)) + (isCorrect ? 1 : 0)) / vocab.attempts;

                    if (vocab.accuracy > 0.8 && vocab.attempts >= 3) {
                        vocab.mastered = true;
                    }
                });
            }

            // Update skills
            if (exercise.prerequisites?.required_skills) {
                exercise.prerequisites.required_skills.forEach(skill => {
                    if (!updated.skills[skill]) {
                        updated.skills[skill] = { mastered: false, attempts: 0, accuracy: 0 };
                    }

                    const skillData = updated.skills[skill];
                    skillData.attempts++;
                    skillData.accuracy = ((skillData.accuracy * (skillData.attempts - 1)) + (isCorrect ? 1 : 0)) / skillData.attempts;

                    if (skillData.accuracy > 0.8 && skillData.attempts >= 3) {
                        skillData.mastered = true;
                    }
                });
            }

            // Update overall level
            const totalMastered = Object.values(updated.grammar_points).filter(p => p.mastered).length +
                Object.values(updated.vocabulary).filter(v => v.mastered).length +
                Object.values(updated.skills).filter(s => s.mastered).length;

            const totalItems = Object.keys(updated.grammar_points).length +
                Object.keys(updated.vocabulary).length +
                Object.keys(updated.skills).length;

            const masteryRatio = totalMastered / totalItems;

            if (masteryRatio > 0.8) {
                updated.overall_level = 'advanced';
            } else if (masteryRatio > 0.5) {
                updated.overall_level = 'intermediate';
            } else {
                updated.overall_level = 'beginner';
            }

            updated.last_updated = new Date().toISOString();

            return updated;
        });
    };

    const checkPrerequisites = (exercise) => {
        if (!exercise.prerequisites) return true;

        const { required_grammar_points = [], required_vocabulary = [], required_skills = [], minimum_level = 'beginner' } = exercise.prerequisites;

        // Check minimum level
        const levelHierarchy = { beginner: 0, intermediate: 1, advanced: 2 };
        if (levelHierarchy[userKnowledge.overall_level] < levelHierarchy[minimum_level]) {
            return false;
        }

        // Check grammar points
        for (const grammarPoint of required_grammar_points) {
            if (!userKnowledge.grammar_points[grammarPoint]?.mastered) {
                return false;
            }
        }

        // Check vocabulary
        for (const vocabCategory of required_vocabulary) {
            if (!userKnowledge.vocabulary[vocabCategory]?.mastered) {
                return false;
            }
        }

        // Check skills
        for (const skill of required_skills) {
            if (!userKnowledge.skills[skill]?.mastered) {
                return false;
            }
        }

        return true;
    };

    return (
        <UserKnowledgeContext.Provider value={{
            userKnowledge,
            updateUserKnowledge,
            checkPrerequisites
        }}>
            {children}
        </UserKnowledgeContext.Provider>
    );
};

export const useUserKnowledge = () => {
    const context = useContext(UserKnowledgeContext);
    if (!context) {
        throw new Error('useUserKnowledge must be used within a UserKnowledgeProvider');
    }
    return context;
};
